import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import SelectField from 'components/SelectField';
import confirm from 'modules/confirm';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import { getHasElementsInArray } from 'utils';
import {
  getDocumentsForInitialValues,
  getNotificationMediumFormValue,
  hasSelectedDocument,
  DOCS_FIELD_NAME,
  CUSTOM_DOCS_FIELD_NAME,
  CUSTOM_NOTE_FIELD_NAME,
  NOTIFICATION_MEDIUM_FIELD_NAME,
} from '../../../../utils';

import './StartFromTemplate.styles.scss';

const TEMPLATE_FIELD_NAME = 'startFromTemplateId';

const StartFromTemplate: React.FC = () => {
  const { rfiTemplates, getRfiTemplateById } = useContext(RfiContext);

  const form = useForm();

  const appliedTemplateIdRef = useRef<string>();

  const { t } = useTranslation();

  const templateOptions = useMemo(
    () => rfiTemplates.map(({ _id, name }) => ({ value: _id, label: name })),
    [rfiTemplates],
  );

  const handleTemplateSelect = useCallback(
    async (templateId: string) => {
      const { values } = form.getState();

      if (
        hasSelectedDocument(values.documents, values.customDocuments) &&
        !(await confirm({
          title: t('replaceYourCurrentSelection'),
          content: t('applyingThisTemplateReplaces'),
          submitBtnText: t('replace'),
        }))
      ) {
        form.change(TEMPLATE_FIELD_NAME, appliedTemplateIdRef.current);
        return;
      }

      const template = getRfiTemplateById(templateId);

      const { documents, customDocuments } = getDocumentsForInitialValues(
        template?.documents || [],
      );

      form.batch(() => {
        form.change(DOCS_FIELD_NAME, documents);
        form.change(CUSTOM_DOCS_FIELD_NAME, customDocuments);
        form.change(CUSTOM_NOTE_FIELD_NAME, template?.customNote);
        form.change(
          NOTIFICATION_MEDIUM_FIELD_NAME,
          getNotificationMediumFormValue(template?.notificationMedium),
        );
      });

      appliedTemplateIdRef.current = templateId;
    },
    [form, getRfiTemplateById, t],
  );

  if (!getHasElementsInArray(rfiTemplates)) return null;

  return (
    <div className="start-from-template">
      <div className="start-from-template__text">
        <p className="title">{t('startFromATemplate')}</p>
        <p className="description">{t('ticksTheDocumentsSavedInIt')}</p>
      </div>

      <Field
        name={TEMPLATE_FIELD_NAME}
        component={SelectField}
        options={templateOptions}
        placeholder={t('selectATemplate')}
        customOnChange={handleTemplateSelect}
      />
    </div>
  );
};

export default StartFromTemplate;
